import React, { useState, useCallback } from 'react';
import { SlideOutTabProps } from './SlideOutTab.types';
import {
    SlideOutContainer,
    SlideOutWrapper,
    ContentContainer,
    StaticTab,
    PanelTab,
    Label,
    Overlay,
    BumpOut,
} from './SlideOutTab.styles';
import { useVisibilityDetection } from './hooks/useVisibilityDetection';
import { useGlobal } from '../../hooks';
import { getPageUrl, useAnalyticsClickEvent } from 'rwl-ui';
import { ClickTargets } from 'rwl-shared/dist/models/analytics';

/**
 * SlideOutTab Component
 *
 * A tab that can slide out from the side of the screen. It can be configured to
 * open from either the left or right side and positioned at the top, middle, or bottom.
 * It also supports custom styling options for the tab background and text.
 *
 * @param props The component props
 * @returns JSX.Element
 */
export const SlideOutTab = ({ children }: SlideOutTabProps): JSX.Element => {
    const { bumpOutTest } = useGlobal();
    const sendClickEvent = useAnalyticsClickEvent();

    if (!bumpOutTest) {
        return <>{children}</>;
    }
    const {
        openPanel,
        tabLocation,
        tabBackgroundColor,
        tabTextColor,
        ctaText,
        font,
    } = bumpOutTest;

    // State to track if the panel is open or closed
    const [isOpen, setIsOpen] = useState(false);
    // State to track hover for StaticTab
    const [isStaticTabHovered, setIsStaticTabHovered] = useState(false);

    // Toggle the open state when the tab is clicked
    const toggleOpen = () => {
        sendClickEvent({
            url: getPageUrl(),
            target: ClickTargets.BUMP_OUT_CLICKED,
        });
        setIsOpen((prev) => !prev);
    };

    // Close panel when clicking outside
    const handleOverlayClick = useCallback(() => {
        setIsOpen(false);
    }, []);

    // Use our custom hook to handle visibility detection
    useVisibilityDetection(isOpen);

    return (
        <>
            {/* Darkened overlay when panel is open */}
            <Overlay
                isOpen={isOpen}
                onClick={handleOverlayClick}
                data-testid="slide-out-overlay"
            />

            {/* BumpOut and StaticTab - visible when panel is closed */}
            <div
                className={isStaticTabHovered ? 'static-tab-hovered' : ''}
                style={{
                    position: 'fixed',
                    top: 0,
                    left: openPanel === 'left' ? 0 : 'auto',
                    right: openPanel === 'right' ? 0 : 'auto',
                    height: '100vh',
                    zIndex: 999,
                }}
            >
                <BumpOut
                    openPanel={openPanel}
                    bumpOutColor={tabBackgroundColor}
                />
                <StaticTab
                    onClick={toggleOpen}
                    data-testid="static-tab-handle"
                    isOpen={isOpen}
                    tabBackgroundColor={tabBackgroundColor}
                    openPanel={openPanel}
                    tabLocation={tabLocation}
                    onMouseEnter={() => setIsStaticTabHovered(true)}
                    onMouseLeave={() => setIsStaticTabHovered(false)}
                >
                    <Label
                        openPanel={openPanel}
                        tabTextColor={tabTextColor}
                        font={font}
                    >
                        {ctaText || 'See work near you'}
                    </Label>
                </StaticTab>
            </div>

            <SlideOutContainer>
                <SlideOutWrapper
                    isOpen={isOpen}
                    data-testid="slide-out-wrapper"
                    tabBackgroundColor={tabBackgroundColor}
                    openPanel={openPanel}
                >
                    <ContentContainer>{children}</ContentContainer>

                    {/* Panel tab - visible when panel is open */}
                    <PanelTab
                        onClick={toggleOpen}
                        data-testid="panel-tab-handle"
                        isOpen={isOpen}
                        tabBackgroundColor={tabBackgroundColor}
                        openPanel={openPanel}
                        tabLocation={tabLocation}
                    >
                        <Label
                            openPanel={openPanel}
                            tabTextColor={tabTextColor}
                            font={font}
                        >
                            {ctaText || 'See work near you'}
                        </Label>
                    </PanelTab>
                </SlideOutWrapper>
            </SlideOutContainer>
        </>
    );
};
